import {useSortable} from '@dnd-kit/sortable'
import {CSS} from '@dnd-kit/utilities'
import {AlertTriangle, GripVertical, X} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Tooltip, TooltipContent, TooltipTrigger} from '@/components/ui/tooltip'
import {Button} from '@/components/ui/button'
import {cn} from '@/lib/utils'
import {resolveFieldLabel} from '@/types/form-layout'
import type {LayoutField} from '@/types/form-layout'
import type {DesignerAction} from '@/types/form-layout'
import type {DesignerSelection} from '@/types/form-layout'
import type {Palette} from '@/types/form-layout'

function isOrphaned(field: LayoutField, palette: Palette | null): boolean {
    if (!palette) return false
    if (field.source === 'entity') {
        return !palette.entityFields.some((f) => f.fieldKey === field.fieldKey)
    }
    if (field.source === 'attribute') {
        return (
            !palette.attributeDefinitions.some((d) => d.id === field.definitionId) &&
            !palette.readOnlyFields.some((d) => d.id === field.definitionId)
        )
    }
    if (field.source === 'relationship') {
        return !palette.relationshipFields.some((f) => f.relationshipType === field.relationshipType)
    }
    return false
}

// ============================================================================
// FieldCard
// ============================================================================

interface FieldCardProps {
    sectionKey: string
    fieldIndex: number
    field: LayoutField
    palette: Palette | null
    dispatch: React.Dispatch<DesignerAction>
    selection: DesignerSelection
    onSelect: (sel: DesignerSelection) => void
    colSpanExceedsRow: boolean
    defaultColSpan: number
}

export function FieldCard({
    sectionKey,
    fieldIndex,
    field,
    palette,
    dispatch,
    selection,
    onSelect,
    colSpanExceedsRow,
    defaultColSpan,
}: FieldCardProps) {
    const i18n = useI18n()

    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        /* eslint-disable i18next/no-literal-string */
        id: `field-${sectionKey}-${fieldIndex}`,
        data: {type: 'field-card', sectionKey, fieldIndex},
        /* eslint-enable i18next/no-literal-string */
    })

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
    }

    const orphaned = isOrphaned(field, palette)
    const label = resolveFieldLabel(field, palette)
    const isSelected =
        selection?.type === 'field' &&
        selection.sectionKey === sectionKey &&
        selection.fieldIndex === fieldIndex

    const colSpan = field.colSpan ?? defaultColSpan

    return (
        <div
            ref={setNodeRef}
            style={{...style, gridColumn: `span ${Math.min(colSpan, 12)}`}}
            className={cn(
                'relative flex items-center gap-2 rounded border bg-background px-3 py-2 text-sm transition-all',
                isSelected && 'ring-2 ring-primary',
                orphaned && 'border-amber-400 bg-amber-50 dark:bg-amber-950/20',
                isDragging && 'opacity-80 shadow-lg scale-[1.02] ring-2 ring-primary/50 z-10',
                !isDragging && 'cursor-grab hover:bg-muted/50 hover:shadow-sm active:cursor-grabbing active:shadow-md active:scale-[1.01]'
            )}
            onClick={() => onSelect({type: 'field', sectionKey, fieldIndex})}
            {...attributes}
            {...listeners}
        >
            {/* Drag indicator */}
            <GripVertical className="h-3 w-3 shrink-0 text-muted-foreground" />

            {/* Label */}
            <span className="flex-1 truncate font-medium">{label}</span>

            {/* Orphaned warning */}
            {orphaned && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <AlertTriangle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent>{i18n.formLayouts.orphanedFieldTooltip}</TooltipContent>
                </Tooltip>
            )}

            {/* ColSpan overflow badge */}
            {colSpanExceedsRow && (
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                            {i18n.formLayouts.colSpanOverflow}
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>{i18n.formLayouts.colSpanOverflowTooltip}</TooltipContent>
                </Tooltip>
            )}

            {/* Remove button */}
            <Button
                data-testid="field-card-remove-btn"
                variant="ghost"
                size="sm"
                className="h-5 w-5 shrink-0 p-0 text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                    e.stopPropagation()
                    dispatch({type: 'REMOVE_FIELD', sectionKey, fieldIndex})
                }}
            >
                <X className="h-3 w-3" />
            </Button>
        </div>
    )
}
